import React, { Component } from "react";
import { Checkbox, Col, Empty, Input, Row, Tag, Tooltip } from "antd";
import { CheckSquareOutlined, CloseSquareOutlined, DownOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Tree, TreeNode } from "react-organizational-chart";
import { ButtonItem, SegmentedItem } from "../../../Components/Items";
import { filtrarCatalogoPorBusqueda } from "../permisosSoloMaster";

const CIAN = "#00BFD6";
const MORADO = "#800080";

const tooltipStyle = { fontSize: "10px", padding: "1px 5px", minHeight: "auto" };

const SIN_SUBGRUPO = { _id: "sinSubgrupo", nombre: "Sin subgrupo" };

// Editor de funciones de IA por grupo y subgrupo, en modo básico (tarjetas) o avanzado (árbol)
class PermisosIaEditor extends Component {
  state = {
    modo: "basico",
    busqueda: "",
    colapsados: [],
  };

  alternar = (id) =>
    this.setState((prev) => ({
      colapsados: prev.colapsados.includes(id) ? prev.colapsados.filter((item) => item !== id) : [...prev.colapsados, id],
    }));

  colapsado = (id) => this.state.colapsados.includes(id) && !this.state.busqueda;

  activo = (clave) => this.props.seleccionados.includes(clave);

  clavesDe = (permisos) => permisos.map((permiso) => permiso._id);

  subgruposDe = (grupo) => {
    const subgrupos = new Map();
    for (const permiso of grupo.permisos) {
      const info = permiso.subgrupo || SIN_SUBGRUPO;
      if (!subgrupos.has(info._id)) subgrupos.set(info._id, { info, permisos: [] });
      subgrupos.get(info._id).permisos.push(permiso);
    }
    return [...subgrupos.values()];
  };

  estado = (permisos) => {
    const activos = permisos.filter((permiso) => this.activo(permiso._id)).length;
    return { activos, todos: activos > 0 && activos === permisos.length, parcial: activos > 0 && activos < permisos.length };
  };

  renderCheckConjunto = (permisos, texto = null) => {
    const estado = this.estado(permisos);
    return (
      <Checkbox
        checked={estado.todos}
        indeterminate={estado.parcial}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => this.props.onToggle(this.clavesDe(permisos), e.target.checked)}
      >
        {texto}
      </Checkbox>
    );
  };

  renderContador = (permisos) => {
    const { activos } = this.estado(permisos);
    return (
      <Tag color={activos > 0 ? "cyan" : "default"} style={{ margin: 0 }}>
        {activos} de {permisos.length}
      </Tag>
    );
  };

  renderFlecha = (colapsado) =>
    colapsado ? <RightOutlined style={{ fontSize: 12, color: "#1a3a4a" }} /> : <DownOutlined style={{ fontSize: 12, color: "#1a3a4a" }} />;

  opcionHabilitada = (permiso) => !permiso.esOpcion || (permiso.aplicaA || []).some((herramienta) => this.activo(herramienta.clave));

  renderTarjeta = (permiso) => {
    const activo = this.activo(permiso._id);
    const habilitada = this.opcionHabilitada(permiso);
    const color = permiso.esOpcion ? MORADO : CIAN;
    const tarjeta = (
      <div
        onClick={() => habilitada && this.props.onToggle([permiso._id], !activo)}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          height: "100%",
          padding: "10px 12px",
          borderRadius: 8,
          cursor: habilitada ? "pointer" : "not-allowed",
          opacity: habilitada ? 1 : 0.55,
          border: `1px ${permiso.esOpcion ? "dashed" : "solid"} ${activo ? color : permiso.esOpcion ? "#c9a3c9" : "rgb(235, 237, 240)"}`,
          background: activo ? (permiso.esOpcion ? "rgba(128, 0, 128, 0.06)" : "rgba(0, 191, 214, 0.08)") : "#fff",
          transition: "all 0.15s ease-out",
        }}
      >
        <Checkbox
          checked={activo}
          disabled={!habilitada}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => this.props.onToggle([permiso._id], e.target.checked)}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 600, color: "#1a3a4a" }}>
            {permiso.nombre}
            {permiso.esOpcion && (
              <Tag color="purple" style={{ marginLeft: 8, fontSize: 11 }}>
                Opción
              </Tag>
            )}
          </span>
          {permiso.descripcion && <span style={{ fontSize: 12, color: "#888" }}>{permiso.descripcion}</span>}
          {permiso.esOpcion && (permiso.aplicaA || []).length > 0 && (
            <span style={{ fontSize: 12, color: MORADO }}>Se agrega a: {permiso.aplicaA.map((herramienta) => herramienta.nombre).join(", ")}</span>
          )}
        </div>
      </div>
    );
    return habilitada ? (
      tarjeta
    ) : (
      <Tooltip title="Activa primero alguna de las herramientas a las que se agrega" placement="top" overlayInnerStyle={tooltipStyle}>
        {tarjeta}
      </Tooltip>
    );
  };

  renderTarjetas = (permisos) => (
    <Row gutter={[10, 10]}>
      {permisos.map((permiso) => (
        <Col key={permiso._id} xs={24} md={12}>
          {this.renderTarjeta(permiso)}
        </Col>
      ))}
    </Row>
  );

  renderSubgrupo = (grupo, sub) => {
    const id = `${grupo._id}|${sub.info._id}`;
    const colapsado = this.colapsado(id);
    const herramientas = sub.permisos.filter((permiso) => !permiso.esOpcion);
    const opciones = sub.permisos.filter((permiso) => permiso.esOpcion);
    return (
      <div key={id} style={{ marginBottom: 12, paddingLeft: 12 }}>
        <div
          onClick={() => this.alternar(id)}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 10px",
            marginBottom: 8,
            borderBottom: "1px solid rgba(0, 191, 214, 0.35)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {this.renderCheckConjunto(sub.permisos)}
            <span style={{ fontWeight: 600, color: "#1a3a4a" }}>{sub.info.nombre}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {this.renderContador(sub.permisos)}
            {this.renderFlecha(colapsado)}
          </div>
        </div>
        {!colapsado && (
          <>
            {herramientas.length > 0 && this.renderTarjetas(herramientas)}
            {opciones.length > 0 && (
              <>
                <div style={{ margin: "12px 0 8px", fontSize: 12, fontWeight: 600, color: MORADO }}>Opciones de las herramientas</div>
                {this.renderTarjetas(opciones)}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  renderBasico = (catalogo) =>
    catalogo.map((grupo) => {
      const colapsado = this.colapsado(grupo._id);
      return (
        <div key={grupo._id} style={{ marginBottom: 14 }}>
          <div
            onClick={() => this.alternar(grupo._id)}
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "6px 12px",
              marginBottom: 8,
              borderRadius: 8,
              background: "rgba(0, 191, 214, 0.1)",
              borderLeft: `4px solid ${CIAN}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {this.renderCheckConjunto(grupo.permisos)}
              <strong style={{ color: "#1a3a4a" }}>{grupo.nombre}</strong>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {this.renderContador(grupo.permisos)}
              {this.renderFlecha(colapsado)}
            </div>
          </div>
          {!colapsado && this.subgruposDe(grupo).map((sub) => this.renderSubgrupo(grupo, sub))}
        </div>
      );
    });

  renderAvanzado = (catalogo) => (
    <div style={{ overflowX: "auto", paddingBottom: 8 }}>
      {catalogo.map((grupo) => (
        <div key={grupo._id} style={{ marginBottom: 24, minWidth: "fit-content" }}>
          <Tree
            lineWidth="2px"
            lineColor={CIAN}
            lineBorderRadius="6px"
            label={
              <div style={{ display: "inline-block", padding: "8px 14px", borderRadius: 8, border: `2px solid ${CIAN}`, background: "#E6FBFD" }}>
                {this.renderCheckConjunto(grupo.permisos, <strong style={{ color: "#1a3a4a" }}>{grupo.nombre}</strong>)}
              </div>
            }
          >
            {this.subgruposDe(grupo).map((sub) => (
              <TreeNode
                key={sub.info._id}
                label={
                  <div style={{ display: "inline-block", padding: "6px 12px", borderRadius: 8, border: `1px solid ${CIAN}`, background: "#fff" }}>
                    {this.renderCheckConjunto(sub.permisos, <strong style={{ color: "#1a3a4a" }}>{sub.info.nombre}</strong>)}
                  </div>
                }
              >
                {sub.permisos.map((permiso) => (
                  <TreeNode key={permiso._id} label={<div style={{ display: "inline-block", minWidth: 200, textAlign: "left" }}>{this.renderTarjeta(permiso)}</div>} />
                ))}
              </TreeNode>
            ))}
          </Tree>
        </div>
      ))}
    </div>
  );

  render() {
    const catalogo = filtrarCatalogoPorBusqueda(this.props.catalogo || [], this.state.busqueda) || [];
    const todasLasClaves = (this.props.catalogo || []).flatMap((grupo) => this.clavesDe(grupo.permisos));

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <SegmentedItem
            options={[
              { label: "Básico", value: "basico" },
              { label: "Avanzado", value: "avanzado" },
            ]}
            value={this.state.modo}
            onChange={(modo) => this.setState({ modo })}
          />
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
            placeholder="Buscar grupo, subgrupo o función"
            value={this.state.busqueda}
            onChange={(e) => this.setState({ busqueda: e.target.value })}
            style={{ flex: 1, minWidth: 180, borderRadius: 8 }}
          />
          <Tooltip title="Seleccionar todas las funciones" placement="top" overlayInnerStyle={tooltipStyle}>
            <ButtonItem type="default" size="small" cyan icon={<CheckSquareOutlined />} onClick={() => this.props.onToggle(todasLasClaves, true)} />
          </Tooltip>
          <Tooltip title="Quitar todas las funciones" placement="top" overlayInnerStyle={tooltipStyle}>
            <ButtonItem type="default" size="small" danger icon={<CloseSquareOutlined />} onClick={() => this.props.onToggle(todasLasClaves, false)} />
          </Tooltip>
        </div>
        {catalogo.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No se encontraron funciones" />
        ) : this.state.modo === "basico" ? (
          this.renderBasico(catalogo)
        ) : (
          this.renderAvanzado(catalogo)
        )}
      </>
    );
  }
}

export default PermisosIaEditor;
